import React, { useEffect, useState } from "react";
import { View, StyleSheet, Image, Pressable, TouchableOpacity, Platform } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import { LinearGradient } from "expo-linear-gradient";
import CheckMark from "../../svgs/common/CheckMark";
import { CloseIcon } from "../../svgIcons";

type Props = {
  imageIndex: number;
  thumbnailUrl: string;
  isApproved: boolean;
  onOpen: () => void;
  onRemove: () => void;
  onToggleApprove: () => void;
};

const ObjectiveImageCard: React.FC<Props> = ({
  imageIndex,
  thumbnailUrl,
  isApproved,
  onOpen,
  onRemove,
  onToggleApprove,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const showOverlayActions = Platform.OS !== "web" || isHovered;
  const [imageAspectRatio, setImageAspectRatio] = useState<number | null>(null);

  useEffect(() => {
    if (!thumbnailUrl) {
      setImageAspectRatio(null);
      return;
    }

    Image.getSize(
      thumbnailUrl,
      (width, height) => {
        if (width > 0 && height > 0) setImageAspectRatio(width / height);
      },
      () => setImageAspectRatio(null)
    );
  }, [thumbnailUrl]);

  return (
    <View style={[styles.card, isApproved ? styles.cardApproved : undefined]}>
      <Pressable
        style={styles.tile}
        onPress={onOpen}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
        accessibilityLabel={`Open image ${imageIndex + 1}`}
        accessibilityRole="button">
        <View style={styles.badge}>
          <CustomText
            text={`${imageIndex + 1}`}
            useTranslationText={false}
            size="xs"
            style={{ color: "#FFFFFF" }}
            weight="bold"
          />
        </View>

        {thumbnailUrl ? (
          <Image
            source={{ uri: thumbnailUrl }}
            style={[styles.thumbnail, imageAspectRatio ? { aspectRatio: imageAspectRatio } : undefined]}
            resizeMode="contain"
          />
        ) : (
          <LinearGradient
            colors={["#EEF2FF", "#E0EAFF", "#F1F5F9"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={[styles.placeholder, imageAspectRatio ? { aspectRatio: imageAspectRatio } : undefined]}
          />
        )}

        {showOverlayActions && (
          <View style={styles.overlayActions}>
            <TouchableOpacity
              style={[styles.overlayIconButton, styles.overlayIconButtonLight]}
              onPress={onRemove}
              aria-label="Remove image">
              <CloseIcon fill="#667085" props={{ width: 18, height: 18 }} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.overlayIconButton, styles.overlayIconButtonLight]}
              onPress={onToggleApprove}
              aria-label={isApproved ? "Unapprove image" : "Approve image"}>
              <CheckMark width={16} height={16} fill={isApproved ? "#209B51" : "#667085"} />
            </TouchableOpacity>
          </View>
        )}
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#fff",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#EAECF0",
    overflow: "hidden",
  },
  cardApproved: {
    borderColor: "#66C61C",
  },
  tile: {
    position: "relative",
  },
  badge: {
    position: "absolute",
    top: 12,
    left: 12,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: "rgba(17,24,39,0.7)",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
  },
  thumbnail: {
    width: "100%",
    height: undefined,
  },
  placeholder: {
    width: "100%",
    height: undefined,
  },
  overlayActions: {
    position: "absolute",
    top: 10,
    right: 10,
    flexDirection: "row",
    gap: 8,
    zIndex: 2,
  },
  overlayIconButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  overlayIconButtonLight: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
});

export default ObjectiveImageCard;
